import { DeviceEventEmitter } from "react-native";

export interface FreeTierLimits {
  productCount: number;
  debtCount: number;
  factureCount: number;
}

interface FreeTierErrorResponse {
  error: string;
  limits: FreeTierLimits;
}

// Simple functional approach - no classes needed!
const EVENTS = {
  FREE_TIER_LIMIT_EXCEEDED: "freeTierLimitExceeded",
  FREE_TIER_STATUS: "freeTierStatus",
} as const;

/**
 * Check if the API response indicates a free tier limit has been exceeded
 */
export const isFreeTierLimitExceeded = (response: any): boolean => {
  return (
    response &&
    response.error &&
    response.error.includes("Free tier limit exceeded") &&
    response.limits
  );
};

/**
 * Check if the API response is related to free tier limits (within limits)
 */
export const isFreeTierResponse = (response: any): boolean => {
  return (
    response &&
    response.message &&
    (response.message.includes("User is within free tier limits") ||
      response.message.includes("User is on a paid plan")) &&
    response.limits !== undefined
  );
};

/**
 * Extract free tier limits from API response
 */
export const extractLimits = (response: any): FreeTierLimits | null => {
  if (response && response.limits) {
    return {
      productCount: response.limits.productCount || 0,
      debtCount: response.limits.debtCount || 0,
      factureCount: response.limits.factureCount || 0,
    };
  }
  return null;
};

/**
 * Check if the current request URL is one of the endpoints that should trigger free tier modal
 */
export const isFreeTierEndpoint = (url: string): boolean => {
  const freeTierEndpoints = [
    "/sell",
    "/sellwithimages",
    "/stock/add",
    "/debt",
    "/debt/facture",
  ];

  return freeTierEndpoints.some((endpoint) => url.includes(endpoint));
};

/**
 * Handle free tier limit exceeded response
 */
export const handleFreeTierLimitExceeded = (
  response: FreeTierErrorResponse
): void => {
  const limits = extractLimits(response);
  DeviceEventEmitter.emit(EVENTS.FREE_TIER_LIMIT_EXCEEDED, { limits });
};

/**
 * Handle API response and emit free tier events if needed
 */
export const handleApiResponse = (url: string, response: any): void => {
  // Only handle responses from free tier endpoints
  if (!isFreeTierEndpoint(url)) {
    return;
  }

  // Check if this is a free tier limit exceeded error
  if (isFreeTierLimitExceeded(response)) {
    handleFreeTierLimitExceeded(response);
    return;
  }

  // Optional: Handle successful free tier responses for analytics/tracking
  if (isFreeTierResponse(response)) {
    const limits = extractLimits(response);
    DeviceEventEmitter.emit(EVENTS.FREE_TIER_STATUS, {
      withinLimits: response.message.includes("within free tier limits"),
      isPaidPlan: response.message.includes("paid plan"),
      limits,
    });
  }
};

/**
 * Subscribe to free tier limit exceeded events
 */
export const onFreeTierLimitExceeded = (
  callback: (data: { limits: FreeTierLimits | null }) => void
) => {
  const subscription = DeviceEventEmitter.addListener(
    EVENTS.FREE_TIER_LIMIT_EXCEEDED,
    callback
  );
  return subscription;
};

/**
 * Subscribe to free tier status events
 */
export const onFreeTierStatus = (
  callback: (data: {
    withinLimits: boolean;
    isPaidPlan: boolean;
    limits: FreeTierLimits | null;
  }) => void
) => {
  const subscription = DeviceEventEmitter.addListener(
    EVENTS.FREE_TIER_STATUS,
    callback
  );
  return subscription;
};

/**
 * Calculate usage percentage for a specific limit
 */
export const calculateUsagePercentage = (
  used: number,
  limit: number
): number => {
  if (limit === 0) return 0; // Unlimited
  return Math.min((used / limit) * 100, 100);
};

/**
 * Get remaining count for a specific limit
 */
export const getRemainingCount = (used: number, limit: number): number => {
  if (limit === 0) return Infinity; // Unlimited
  return Math.max(limit - used, 0);
};

/**
 * Check if a limit is near capacity (>= 70%)
 */
export const isNearLimit = (used: number, limit: number): boolean => {
  return calculateUsagePercentage(used, limit) >= 70;
};

/**
 * Check if approaching limit (>= 90% but < 100%)
 */
export const isApproachingLimit = (used: number, limit: number): boolean => {
  const percentage = calculateUsagePercentage(used, limit);
  return percentage >= 90 && percentage < 100;
};

/**
 * Check if a limit is exceeded (>= 100%)
 */
export const isLimitExceeded = (used: number, limit: number): boolean => {
  return calculateUsagePercentage(used, limit) >= 100;
};

/**
 * Get status color based on usage percentage
 */
export const getUsageStatusColor = (used: number, limit: number): string => {
  const percentage = calculateUsagePercentage(used, limit);
  if (percentage >= 100) return "#EF4444"; // Red - only when actually exceeded
  if (percentage >= 90) return "#F59E0B"; // Orange - approaching limit
  if (percentage >= 70) return "#F59E0B"; // Orange
  return "#10B981"; // Green
};

/**
 * Format limits data for display
 */
export const formatLimitsForDisplay = (
  limits: FreeTierLimits,
  isPremium: boolean = false
) => {
  const freeLimits = {
    products: 50,
    debts: 50,
    invoices: 50,
  };

  return {
    products: {
      used: limits.productCount,
      limit: isPremium ? 0 : freeLimits.products,
      percentage: calculateUsagePercentage(
        limits.productCount,
        isPremium ? 0 : freeLimits.products
      ),
      remaining: getRemainingCount(
        limits.productCount,
        isPremium ? 0 : freeLimits.products
      ),
      isNearLimit: isNearLimit(
        limits.productCount,
        isPremium ? 0 : freeLimits.products
      ),
      isApproachingLimit: isApproachingLimit(
        limits.productCount,
        isPremium ? 0 : freeLimits.products
      ),
      isExceeded: isLimitExceeded(
        limits.productCount,
        isPremium ? 0 : freeLimits.products
      ),
    },
    debts: {
      used: limits.debtCount,
      limit: isPremium ? 0 : freeLimits.debts,
      percentage: calculateUsagePercentage(
        limits.debtCount,
        isPremium ? 0 : freeLimits.debts
      ),
      remaining: getRemainingCount(
        limits.debtCount,
        isPremium ? 0 : freeLimits.debts
      ),
      isNearLimit: isNearLimit(
        limits.debtCount,
        isPremium ? 0 : freeLimits.debts
      ),
      isApproachingLimit: isApproachingLimit(
        limits.debtCount,
        isPremium ? 0 : freeLimits.debts
      ),
      isExceeded: isLimitExceeded(
        limits.debtCount,
        isPremium ? 0 : freeLimits.debts
      ),
    },
    invoices: {
      used: limits.factureCount,
      limit: isPremium ? 0 : freeLimits.invoices,
      percentage: calculateUsagePercentage(
        limits.factureCount,
        isPremium ? 0 : freeLimits.invoices
      ),
      remaining: getRemainingCount(
        limits.factureCount,
        isPremium ? 0 : freeLimits.invoices
      ),
      isNearLimit: isNearLimit(
        limits.factureCount,
        isPremium ? 0 : freeLimits.invoices
      ),
      isApproachingLimit: isApproachingLimit(
        limits.factureCount,
        isPremium ? 0 : freeLimits.invoices
      ),
      isExceeded: isLimitExceeded(
        limits.factureCount,
        isPremium ? 0 : freeLimits.invoices
      ),
    },
  };
};

// Export the events object for use in other files
export { EVENTS };
